import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tag, BarChart3, AlertTriangle, Play, Info, Download, Lightbulb, Box, Radio, Zap } from 'lucide-react';
import { apiClient } from '../../api/client';
import { compareDecimalStrings, formatDecimalPercent } from '../../shared/domain/decimal';
import { Lifecycle5State } from '../../components/common/QuantitativeDataTable';
import { CANONICAL_SPOT_SYMBOLS, CanonicalSpotSymbol } from '../../shared/constants/watchlist';

export type MatrixSortField =
  | 'tag_code'
  | 'matched_event_count'
  | 'favorable_proportion'
  | 'profit_factor'
  | 'net_return_pct'
  | 'forward_expectancy_bps';

export type MatrixSortOrder = 'asc' | 'desc';

export interface TagMatrixEntry {
  tag_id: number;
  tag_code: string;
  category: string;
  sample_bars: number;
  matched_event_count: number;
  favorable_proportion: string | null;
  profit_factor: string | null;
  net_return_pct: string | null;
  forward_expectancy_bps: string | null;
}

export interface TagMatrixSummaryResponse {
  status: Lifecycle5State;
  status_reason?: string;
  symbol: string;
  timeframe: string;
  tactical_direction: string;
  lookback_days: number;
  holding_bars: number;
  total_tags_evaluated: number;
  dictionary_digest: string;
  generated_at_utc: string;
  source_watermark_utc: string | null;
  tag_matrix: TagMatrixEntry[];
}

export interface TagAttributionResponse {
  status: Lifecycle5State;
  status_reason: string;
  request_fingerprint: string;
  echo_canonical_preimage: string;
  sample_bars: number;
  matched_event_count: number;
  favorable_proportion: string | null;
  profit_factor: string | null;
  gross_return_pct: string | null;
  net_return_pct: string | null;
  max_drawdown_pct: string | null;
  forward_expectancy_bps: string | null;
  dispersion_mean_bps: string | null;
  dispersion_std_bps: string | null;
  notional_reference_index_series: [string, string][];
  dictionary_digest: string;
  friction_policy_version: string;
  source_watermark_utc: string | null;
  generated_at_utc: string;
}

interface TagAttributionLabTabProps {
  initialSymbol?: CanonicalSpotSymbol | string;
  initialTimeframe?: string;
  compactMode?: boolean;
}

export const TIMEFRAME_SECONDS_MAP: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
};

export function computeAlignedTimeframeBounds(
  nowMs: number,
  timeframe: string,
  lookbackDays: number
): { startTimeUtc: string; endTimeUtc: string; barSeconds: number } {
  const barSec = TIMEFRAME_SECONDS_MAP[timeframe.toLowerCase()] || 1800;
  const nowSec = Math.floor(nowMs / 1000);
  const endSec = Math.floor(nowSec / barSec) * barSec;
  const lookbackSec = lookbackDays * 86400;
  const startSec = endSec - lookbackSec;

  return {
    startTimeUtc: new Date(startSec * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z'),
    endTimeUtc: new Date(endSec * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z'),
    barSeconds: barSec,
  };
}

const PRESET_FACT_TAGS = [
  { code: 'FACT_RSI_OVERSOLD', name: 'RSI超卖探底', category: 'PRICE_ACTION' },
  { code: 'FACT_FIB_0618_TOUCH', name: '触及0.618回撤', category: 'PRICE_ACTION' },
  { code: 'FACT_SHRINK_PULLBACK_MA20', name: '缩量回踩MA20', category: 'PRICE_ACTION' },
  { code: 'FACT_BROKEN_MA20_LINE', name: '破位MA20均线', category: 'PRICE_ACTION' },
  { code: 'FACT_REBOUND_VOLUME_DROUGHT', name: '反弹量能枯竭', category: 'PRICE_ACTION' },
  { code: 'FACT_LOWER_HIGHS_EXHAUSTION', name: '高点下移势竭', category: 'PRICE_ACTION' },
  { code: 'FACT_HTF_SUPPORT_VACUUM_ROOM', name: '4H下轨真空区', category: 'MULTI_TIMEFRAME' },
  { code: 'FACT_MACD_GOLDEN_CROSS', name: 'MACD金叉上攻', category: 'PRICE_ACTION' },
];

export const TagAttributionLabTab: React.FC<TagAttributionLabTabProps> = ({
  initialSymbol = 'BTCUSDT',
  initialTimeframe = '5m',
  compactMode = false,
}) => {
  const [symbol, setSymbol] = useState<string>(initialSymbol);
  const [timeframe, setTimeframe] = useState<string>(initialTimeframe);
  const [direction, setDirection] = useState<'LONG' | 'SHORT' | 'NEUTRAL'>('LONG');
  const [holdingBars, setHoldingBars] = useState<number>(12);
  const [activeSubTab, setActiveSubTab] = useState<'matrix' | 'sandbox' | 'dataset'>('matrix');

  // Matrix Summary State
  const [matrixState, setMatrixState] = useState<Lifecycle5State>('loading');
  const [matrixData, setMatrixData] = useState<TagMatrixSummaryResponse | null>(null);
  const [matrixFilter, setMatrixFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  // Matrix Sorting & Result Filter States
  const [sortField, setSortField] = useState<MatrixSortField>('matched_event_count');
  const [sortOrder, setSortOrder] = useState<MatrixSortOrder>('desc');
  const [onlyWithEvents, setOnlyWithEvents] = useState<boolean>(false);

  const handleHeaderSort = (field: MatrixSortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'tag_code' ? 'asc' : 'desc');
    }
  };

  // Deep Sandbox Evaluator State
  const [selectedTags, setSelectedTags] = useState<string[]>(['FACT_RSI_OVERSOLD']);
  const [customTagInput, setCustomTagInput] = useState<string>('');
  const [combinationMode, setCombinationMode] = useState<'AND' | 'OR'>('AND');
  const [dedupMode, setDedupMode] = useState<'SKIP_OVERLAPPING' | 'ALLOW_OVERLAPPING'>('SKIP_OVERLAPPING');
  const [executionModel, setExecutionModel] = useState<'close_to_close' | 'atr_discrete'>('close_to_close');
  const [atrTpMultiple, setAtrTpMultiple] = useState<string>('2.0');
  const [atrSlMultiple, setAtrSlMultiple] = useState<string>('1.0');
  const [lookbackPreset, setLookbackPreset] = useState<number>(7);
  const [evaluatorState, setEvaluatorState] = useState<Lifecycle5State>('empty');
  const [evaluatorReport, setEvaluatorReport] = useState<TagAttributionResponse | null>(null);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);

  // Sync initial props
  useEffect(() => {
    if (initialSymbol) setSymbol(initialSymbol);
  }, [initialSymbol]);

  useEffect(() => {
    if (initialTimeframe) setTimeframe(initialTimeframe);
  }, [initialTimeframe]);

  // Fetch Matrix Summary
  const fetchMatrixSummary = useCallback(async () => {
    setMatrixState('loading');
    try {
      const endpoint = `/api/v1/lab/tag-matrix-summary?symbol=${symbol}&timeframe=${timeframe}&direction=${direction}&holding_bars=${holdingBars}&lookback_days=${lookbackPreset}`;
      const res = await apiClient.get<TagMatrixSummaryResponse>(endpoint);
      setMatrixData(res);
      setMatrixState(res?.status || 'disconnected');
    } catch (err) {
      console.warn('Failed to fetch tag matrix summary:', err);
      setMatrixData(null);
      setMatrixState('disconnected');
    }
  }, [symbol, timeframe, direction, holdingBars, lookbackPreset]);

  // Initial and reactive matrix fetch
  useEffect(() => {
    if (activeSubTab === 'matrix') {
      fetchMatrixSummary();
    }
  }, [activeSubTab, fetchMatrixSummary]);

  // Run Attribution Evaluation
  const runAttributionEvaluation = useCallback(async () => {
    const tagsToQuery = selectedTags.length > 0 ? selectedTags : ['FACT_RSI_OVERSOLD'];
    setIsEvaluating(true);
    setEvaluatorState('loading');
    try {
      const { startTimeUtc, endTimeUtc } = computeAlignedTimeframeBounds(Date.now(), timeframe, lookbackPreset);

      const params = new URLSearchParams({
        symbol,
        timeframe,
        start_time: startTimeUtc,
        end_time: endTimeUtc,
        direction,
        tags: tagsToQuery.join(','),
        mode: combinationMode,
        horizon: String(holdingBars),
        dedup: dedupMode,
        notional_base_capital: '1000.00',
        execution_model: executionModel,
        atr_tp_multiple: atrTpMultiple,
        atr_sl_multiple: atrSlMultiple,
      });

      const endpoint = `/api/v1/lab/tag-attribution?${params.toString()}`;
      const res = await apiClient.get<TagAttributionResponse>(endpoint);
      setEvaluatorReport(res);
      setEvaluatorState(res?.status || 'disconnected');
    } catch (err) {
      console.warn('Failed to run tag attribution evaluation:', err);
      setEvaluatorReport(null);
      setEvaluatorState('disconnected');
    } finally {
      setIsEvaluating(false);
    }
  }, [symbol, timeframe, direction, selectedTags, combinationMode, holdingBars, dedupMode, executionModel, atrTpMultiple, atrSlMultiple, lookbackPreset]);

  // Reset evaluator when parameters change
  const resetEvaluator = useCallback(() => {
    setEvaluatorReport(null);
    setEvaluatorState('empty');
  }, []);

  // Toggle tag selection
  const toggleTag = (code: string) => {
    resetEvaluator();
    setSelectedTags((prev) =>
      prev.includes(code) ? prev.filter((t) => t !== code) : [...prev, code]
    );
  };

  // Add custom tag from text input
  const addCustomTag = () => {
    const clean = customTagInput.trim().toUpperCase();
    if (clean && !selectedTags.includes(clean)) {
      resetEvaluator();
      setSelectedTags((prev) => [...prev, clean]);
      setCustomTagInput('');
    }
  };

  // Smart sorted & filtered matrix rows
  const displayedMatrixRows = useMemo(() => {
    const rawRows = (matrixData?.tag_matrix || []).filter((row) => {
      if (onlyWithEvents && row.matched_event_count === 0) {
        return false;
      }
      if (categoryFilter !== 'ALL' && row.category.toUpperCase() !== categoryFilter) {
        return false;
      }
      if (matrixFilter.trim()) {
        const q = matrixFilter.trim().toUpperCase();
        return row.tag_code.toUpperCase().includes(q) || row.category.toUpperCase().includes(q);
      }
      return true;
    });

    return [...rawRows].sort((a, b) => {
      // 1. Auto-prioritize: rows with events > 0 always come before 0-event rows unless sorting explicitly by tag_code
      if (sortField !== 'tag_code') {
        const aHasData = a.matched_event_count > 0;
        const bHasData = b.matched_event_count > 0;
        if (aHasData !== bHasData) {
          return aHasData ? -1 : 1;
        }
      }

      // 2. Column sort
      let comp = 0;
      if (sortField === 'tag_code') {
        comp = a.tag_code.localeCompare(b.tag_code);
      } else if (sortField === 'matched_event_count') {
        comp = a.matched_event_count - b.matched_event_count;
      } else if (sortField === 'favorable_proportion') {
        const valA = a.favorable_proportion ? parseFloat(a.favorable_proportion) : -Infinity;
        const valB = b.favorable_proportion ? parseFloat(b.favorable_proportion) : -Infinity;
        comp = valA - valB;
      } else if (sortField === 'profit_factor') {
        const valA = a.profit_factor ? parseFloat(a.profit_factor) : -Infinity;
        const valB = b.profit_factor ? parseFloat(b.profit_factor) : -Infinity;
        comp = valA - valB;
      } else if (sortField === 'net_return_pct') {
        const valA = a.net_return_pct ? parseFloat(a.net_return_pct) : -Infinity;
        const valB = b.net_return_pct ? parseFloat(b.net_return_pct) : -Infinity;
        comp = valA - valB;
      } else if (sortField === 'forward_expectancy_bps') {
        const valA = a.forward_expectancy_bps ? parseFloat(a.forward_expectancy_bps) : -Infinity;
        const valB = b.forward_expectancy_bps ? parseFloat(b.forward_expectancy_bps) : -Infinity;
        comp = valA - valB;
      }

      return sortOrder === 'desc' ? -comp : comp;
    });
  }, [matrixData?.tag_matrix, categoryFilter, matrixFilter, onlyWithEvents, sortField, sortOrder]);

  const isMatrixFreshDirectional = matrixState === 'fresh' && direction !== 'NEUTRAL';
  const isFreshDirectional = evaluatorReport?.status === 'fresh' && direction !== 'NEUTRAL';

  return (
    <div
      className="tag-attribution-lab-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: compactMode ? '0px' : '6px 0',
      }}
    >
      {/* 1. Lab Context & Global Controls Card */}
      <div
        className="indicator-resonance-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '10px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '8px',
          padding: '12px 16px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', width: '100%' }}>
          <div className="bilingual-view-header">
            <div className="bilingual-header-top">
              <Tag size={18} style={{ color: 'var(--accent-cyan)' }} />
              <h3 className="bilingual-header-title-zh">
                因子归因实验室
              </h3>
              <span
                className="badge"
                style={{
                  fontSize: '10px',
                  background: 'var(--badge-info-bg)',
                  color: 'var(--accent-cyan)',
                  border: '1px solid var(--badge-info-border)',
                }}
              >
                W ≥ 3.0s 因果闭合
              </span>
              <span
                className="badge"
                style={{
                  fontSize: '10px',
                  background: 'var(--badge-neutral-bg)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--badge-neutral-border)',
                }}
              >
                -14.0 bps 摩擦
              </span>
            </div>
            <span className="bilingual-header-subtitle-en">TAG ATTRIBUTION LAB & EPHEMERAL ENGINE</span>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              微观事实标签单/多因子组合归因 · 纯只读离线沙箱推演 · 严禁实盘策略宣称
            </div>
          </div>

          {/* Sub-tab Navigation */}
          <div className="market-type-toggle" style={{ margin: 0, display: 'flex', gap: '4px' }}>
            <button
              className={`toggle-btn bilingual-tab-btn ${activeSubTab === 'matrix' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('matrix')}
              id="btn-subtab-matrix"
              style={{ minHeight: '42px', padding: '4px 12px' }}
            >
              <div className="bilingual-tab-title-zh" style={{ fontSize: '13px' }}>
                <BarChart3 size={12} />
                <span>标签归因矩阵</span>
              </div>
              <span className="bilingual-tab-subtitle-en" style={{ fontSize: '7px' }}>MATRIX SUMMARY</span>
            </button>
            <button
              className={`toggle-btn bilingual-tab-btn ${activeSubTab === 'sandbox' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('sandbox')}
              id="btn-subtab-sandbox"
              style={{ minHeight: '42px', padding: '4px 12px' }}
            >
              <div className="bilingual-tab-title-zh" style={{ fontSize: '13px' }}>
                <Play size={12} />
                <span>组合沙箱推演</span>
              </div>
              <span className="bilingual-tab-subtitle-en" style={{ fontSize: '7px' }}>ATTRIBUTION SANDBOX</span>
            </button>
            <button
              className={`toggle-btn bilingual-tab-btn ${activeSubTab === 'dataset' ? 'active' : ''}`}
              onClick={() => setActiveSubTab('dataset')}
              id="btn-subtab-dataset"
              style={{ minHeight: '42px', padding: '4px 12px' }}
            >
              <div className="bilingual-tab-title-zh" style={{ fontSize: '13px' }}>
                <Box size={12} />
                <span>AI 数据集导出</span>
              </div>
              <span className="bilingual-tab-subtitle-en" style={{ fontSize: '7px' }}>AI DATASET EXPORT</span>
            </button>
          </div>
        </div>

        {/* Global Parameter Selectors Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            paddingTop: '8px',
            borderTop: '1px solid var(--border-subtle)',
            width: '100%',
          }}
        >
          {/* Symbol Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>标的:</span>
            <select
              value={symbol}
              onChange={(e) => {
                setSymbol(e.target.value);
                resetEvaluator();
              }}
              className="regime-search-input"
              style={{ height: '26px', fontSize: '11px', padding: '0 6px', width: '100px' }}
            >
              {CANONICAL_SPOT_SYMBOLS.map((sym) => (
                <option key={sym} value={sym}>
                  {sym.replace('USDT', '')}/USDT
                </option>
              ))}
            </select>
          </div>

          {/* Timeframe Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>周期:</span>
            <div className="market-type-toggle" style={{ margin: 0 }}>
              {(['5m', '15m', '30m', '1h', '4h', '1d'] as const).map((tf) => (
                <button
                  key={tf}
                  onClick={() => {
                    setTimeframe(tf);
                    resetEvaluator();
                  }}
                  className={`toggle-btn ${timeframe === tf ? 'active' : ''}`}
                  style={{ height: '24px', padding: '0 8px', fontSize: '10px' }}
                >
                  {tf}
                </button>
              ))}
            </div>
          </div>

          {/* Direction Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>方向:</span>
            <div className="market-type-toggle" style={{ margin: 0 }}>
              {(['LONG', 'SHORT', 'NEUTRAL'] as const).map((dir) => (
                <button
                  key={dir}
                  onClick={() => {
                    setDirection(dir);
                    resetEvaluator();
                  }}
                  className={`toggle-btn ${direction === dir ? 'active' : ''}`}
                  style={{
                    height: '24px',
                    padding: '0 8px',
                    fontSize: '10px',
                    color:
                      dir === 'LONG'
                        ? 'var(--color-bullish)'
                        : dir === 'SHORT'
                        ? 'var(--color-bearish)'
                        : 'var(--text-secondary)',
                  }}
                >
                  {dir}
                </button>
              ))}
            </div>
          </div>

          {/* Horizon (Holding Bars) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>持仓步长:</span>
            <div className="market-type-toggle" style={{ margin: 0 }}>
              {([6, 12, 24, 48] as const).map((h) => (
                <button
                  key={h}
                  onClick={() => {
                    setHoldingBars(h);
                    resetEvaluator();
                  }}
                  className={`toggle-btn ${holdingBars === h ? 'active' : ''}`}
                  style={{ height: '24px', padding: '0 8px', fontSize: '10px' }}
                >
                  {h} Bars
                </button>
              ))}
            </div>
          </div>

          {/* Lookback Window */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>回溯窗口:</span>
            <select
              value={lookbackPreset}
              onChange={(e) => {
                const val = e.target.value === '30' ? 30 : e.target.value === '14' ? 14 : e.target.value === '3' ? 3 : 7;
                setLookbackPreset(val);
                resetEvaluator();
              }}
              className="regime-search-input"
              style={{ height: '26px', fontSize: '11px', padding: '0 6px', width: '90px' }}
            >
              <option value={3}>近 3 天</option>
              <option value={7}>近 7 天</option>
              <option value={14}>近 14 天</option>
              <option value={30}>近 30 天</option>
            </select>
          </div>

          {/* Refresh / Re-eval button */}
          <button
            onClick={() => {
              if (activeSubTab === 'matrix') {
                fetchMatrixSummary();
              } else if (activeSubTab === 'sandbox') {
                runAttributionEvaluation();
              }
            }}
            className="toggle-btn"
            style={{
              height: '26px',
              padding: '0 12px',
              fontSize: '11px',
              background: 'var(--bg-canvas)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--accent-cyan)',
              marginLeft: 'auto',
            }}
            title="刷新数据"
          >
            ↻ 刷新
          </button>
        </div>
      </div>

      {/* 2. SUB-TAB 1: TAG MATRIX SUMMARY */}
      {activeSubTab === 'matrix' && (
        <section className="regime-table-card">
          {/* Header Bar */}
          <div className="regime-table-header-bar" style={{ flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--accent-cyan)', fontSize: '14px' }}>📋</span>
              <div className="bilingual-card-title">
                <span className="bilingual-card-title-zh">
                  因子态势归因矩阵
                </span>
                <span className="bilingual-card-subtitle-en">
                  TAG MATRIX SUMMARY FOR {symbol.replace('USDT', '')} · {direction}
                </span>
              </div>
              <span
                style={{
                  fontSize: '10px',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  background: 'var(--bg-canvas)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                已评估 {matrixData?.total_tags_evaluated ?? 0} 个标签
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Only With Results Pill */}
              <button
                type="button"
                onClick={() => setOnlyWithEvents((prev) => !prev)}
                className={`toggle-btn ${onlyWithEvents ? 'active' : ''}`}
                style={{
                  height: '22px',
                  fontSize: '10px',
                  padding: '0 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: onlyWithEvents ? 700 : 500,
                  color: onlyWithEvents ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                  borderColor: onlyWithEvents ? 'var(--accent-cyan)' : 'var(--border-subtle)',
                  background: onlyWithEvents ? 'var(--badge-info-bg)' : 'transparent',
                }}
                title="优先并仅显示存在事件触发/计算结果的活跃标签"
              >
                <Zap size={11} />
                <span>{onlyWithEvents ? '仅看有结果 (开启)' : '仅看有结果'}</span>
              </button>

              {/* Category Filter Pills */}
              <div className="market-type-toggle" style={{ margin: 0 }}>
                {(['ALL', 'PRICE_ACTION', 'MULTI_TIMEFRAME'] as const).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`toggle-btn ${categoryFilter === cat ? 'active' : ''}`}
                    style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                  >
                    {cat === 'ALL' ? '全部维度' : cat}
                  </button>
                ))}
              </div>

              {/* Search Box */}
              <input
                type="text"
                placeholder="搜索标签代码..."
                value={matrixFilter}
                onChange={(e) => setMatrixFilter(e.target.value)}
                className="regime-search-input"
                style={{ width: '130px', height: '24px', fontSize: '11px' }}
              />
            </div>
          </div>

          {/* Truthful 5-State Handling */}
          {matrixState === 'loading' ? (
            <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
              正在连接时序沙箱并遍历全量事实标签...
            </div>
          ) : matrixState === 'disconnected' ? (
            <div
              style={{
                padding: '14px',
                margin: '12px',
                borderRadius: '6px',
                background: 'var(--alert-bearish-bg)',
                border: '1px solid var(--alert-bearish-border)',
                color: 'var(--color-rose)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertTriangle size={14} />
              <span>时序沙箱服务未就绪或时序连接中断 (DISCONNECTED) · 请检查底层 TimescaleDB 服务或 API 网关</span>
            </div>
          ) : displayedMatrixRows.length === 0 ? (
            <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
              暂无匹配的事实标签归因数据 (EMPTY)
            </div>
          ) : (
            <div className="regime-table-scroll-wrapper">
              <table className="regime-decision-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleHeaderSort('tag_code')}
                      style={{ minWidth: '180px', cursor: 'pointer', userSelect: 'none' }}
                      title="点击按标签名称排序"
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span>事实标签代码 / 维度</span>
                        <span style={{ fontSize: '10px', color: sortField === 'tag_code' ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                          {sortField === 'tag_code' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleHeaderSort('matched_event_count')}
                      style={{ minWidth: '100px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                      title="点击按事件触发数排序 (默认优先展示有结果的)"
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                        <span>样本柱 / 事件数</span>
                        <span style={{ fontSize: '10px', color: sortField === 'matched_event_count' ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                          {sortField === 'matched_event_count' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleHeaderSort('favorable_proportion')}
                      style={{ minWidth: '110px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      title="点击按滚动胜率排序"
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                        <span>滚动胜率</span>
                        <span style={{ fontSize: '10px', color: sortField === 'favorable_proportion' ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                          {sortField === 'favorable_proportion' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleHeaderSort('profit_factor')}
                      style={{ minWidth: '110px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      title="点击按获利因子 (PF) 排序"
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                        <span>获利因子 (PF)</span>
                        <span style={{ fontSize: '10px', color: sortField === 'profit_factor' ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                          {sortField === 'profit_factor' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleHeaderSort('net_return_pct')}
                      style={{ minWidth: '110px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      title="点击按净收益率排序"
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                        <span>净收益率 (-14bps)</span>
                        <span style={{ fontSize: '10px', color: sortField === 'net_return_pct' ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                          {sortField === 'net_return_pct' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleHeaderSort('forward_expectancy_bps')}
                      style={{ minWidth: '120px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      title="点击按前向期望值排序"
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                        <span>前向期望值 (bps)</span>
                        <span style={{ fontSize: '10px', color: sortField === 'forward_expectancy_bps' ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                          {sortField === 'forward_expectancy_bps' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </div>
                    </th>
                    <th style={{ minWidth: '100px', textAlign: 'center' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedMatrixRows.map((row) => {
                    const winRateStr = isMatrixFreshDirectional && row.favorable_proportion ? formatDecimalPercent(row.favorable_proportion) : '—';
                    const profitFactorStr = isMatrixFreshDirectional ? (row.profit_factor ?? '—') : '—';
                    const netReturnStr = isMatrixFreshDirectional && row.net_return_pct ? `${row.net_return_pct}%` : '—';
                    const expectancyStr = isMatrixFreshDirectional && row.forward_expectancy_bps ? `${row.forward_expectancy_bps} bp` : '—';
                    const isPositive = row.net_return_pct && !row.net_return_pct.startsWith('-');

                    return (
                      <tr
                        key={row.tag_code}
                        style={{
                          opacity: row.matched_event_count === 0 ? 0.6 : 1,
                          background: row.matched_event_count > 0 ? 'var(--bg-canvas)' : 'transparent',
                        }}
                      >
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '12px', color: 'var(--text-primary)' }}>
                              {row.tag_code}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              {row.category}
                            </span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{row.sample_bars}</span>
                          <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
                          <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>{row.matched_event_count}</span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '12px' }}>
                          <span style={{ color: isMatrixFreshDirectional && row.favorable_proportion ? 'var(--color-bullish)' : 'var(--text-muted)' }}>
                            {winRateStr}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                          <span style={{ color: isMatrixFreshDirectional && row.profit_factor ? 'var(--accent-cyan)' : 'var(--text-muted)' }}>
                            {profitFactorStr}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '12px' }}>
                          <span style={{ color: !isMatrixFreshDirectional || !row.net_return_pct ? 'var(--text-muted)' : isPositive ? 'var(--color-bullish)' : 'var(--color-bearish)' }}>
                            {netReturnStr}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {expectancyStr}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => {
                              setSelectedTags([row.tag_code]);
                              setActiveSubTab('sandbox');
                            }}
                            className="toggle-btn"
                            style={{
                              padding: '2px 8px',
                              fontSize: '10px',
                              color: 'var(--accent-cyan)',
                              borderColor: 'var(--badge-info-border)',
                              background: 'var(--bg-canvas)',
                            }}
                            title="将此标签载入沙箱进行深层推演"
                          >
                            🧪 沙箱推演
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Dictionary Digest & Source Watermark Strip */}
          <div
            style={{
              padding: '8px 14px',
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface)',
              fontSize: '10px',
              color: 'var(--text-muted)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontFamily: 'var(--font-mono)',
            }}
          >
            <span>字典摘要: {matrixData?.dictionary_digest?.slice(0, 16) || '—'}...</span>
            <span>时序水印: {matrixData?.source_watermark_utc || '—'} · 因果时延守卫 (W ≥ 3.0s)</span>
          </div>
        </section>
      )}

      {/* 3. SUB-TAB 2: ATTRIBUTION SANDBOX EVALUATOR */}
      {activeSubTab === 'sandbox' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Tag Selector & Multi-Factor Config Card */}
          <div
            className="regime-table-card"
            style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="bilingual-card-title">
                <span className="bilingual-card-title-zh" style={{ fontWeight: 700 }}>
                  选择参与归因推演的事实标签
                </span>
                <span className="bilingual-card-subtitle-en">PRESET FACT TAGS & COMBINATIONS</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>组合逻辑:</span>
                  <div className="market-type-toggle" style={{ margin: 0 }}>
                    <button
                      className={`toggle-btn ${combinationMode === 'AND' ? 'active' : ''}`}
                      onClick={() => {
                        setCombinationMode('AND');
                        resetEvaluator();
                      }}
                      style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                    >
                      AND (共振)
                    </button>
                    <button
                      className={`toggle-btn ${combinationMode === 'OR' ? 'active' : ''}`}
                      onClick={() => {
                        setCombinationMode('OR');
                        resetEvaluator();
                      }}
                      style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                    >
                      OR (任一)
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>去重模式:</span>
                  <div className="market-type-toggle" style={{ margin: 0 }}>
                    <button
                      className={`toggle-btn ${dedupMode === 'SKIP_OVERLAPPING' ? 'active' : ''}`}
                      onClick={() => {
                        setDedupMode('SKIP_OVERLAPPING');
                        resetEvaluator();
                      }}
                      style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                    >
                      SKIP_OVERLAPPING
                    </button>
                    <button
                      className={`toggle-btn ${dedupMode === 'ALLOW_OVERLAPPING' ? 'active' : ''}`}
                      onClick={() => {
                        setDedupMode('ALLOW_OVERLAPPING');
                        resetEvaluator();
                      }}
                      style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                    >
                      ALLOW
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>执行模型:</span>
                  <div className="market-type-toggle" style={{ margin: 0 }}>
                    <button
                      className={`toggle-btn ${executionModel === 'close_to_close' ? 'active' : ''}`}
                      onClick={() => {
                        setExecutionModel('close_to_close');
                        resetEvaluator();
                      }}
                      style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                    >
                      C2C收盘
                    </button>
                    <button
                      className={`toggle-btn ${executionModel === 'atr_discrete' ? 'active' : ''}`}
                      onClick={() => {
                        setExecutionModel('atr_discrete');
                        resetEvaluator();
                      }}
                      style={{ height: '22px', fontSize: '10px', padding: '0 6px' }}
                    >
                      ATR离散
                    </button>
                  </div>
                </div>

                {executionModel === 'atr_discrete' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>TP:</span>
                    <input
                      type="text"
                      value={atrTpMultiple}
                      onChange={(e) => {
                        setAtrTpMultiple(e.target.value);
                        resetEvaluator();
                      }}
                      style={{ width: '36px', height: '20px', fontSize: '10px', padding: '1px 4px', textAlign: 'center', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)', borderRadius: '4px', color: 'var(--text-primary)' }}
                    />
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>SL:</span>
                    <input
                      type="text"
                      value={atrSlMultiple}
                      onChange={(e) => {
                        setAtrSlMultiple(e.target.value);
                        resetEvaluator();
                      }}
                      style={{ width: '36px', height: '20px', fontSize: '10px', padding: '1px 4px', textAlign: 'center', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)', borderRadius: '4px', color: 'var(--text-primary)' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Tag Pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {PRESET_FACT_TAGS.map((tag) => {
                const isSelected = selectedTags.includes(tag.code);
                return (
                  <button
                    key={tag.code}
                    onClick={() => toggleTag(tag.code)}
                    className="toggle-btn"
                    style={{
                      height: '28px',
                      padding: '0 10px',
                      fontSize: '11px',
                      borderRadius: '6px',
                      background: isSelected ? 'var(--badge-info-bg)' : 'var(--bg-canvas)',
                      borderColor: isSelected ? 'var(--accent-cyan)' : 'var(--border-subtle)',
                      color: isSelected ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                      fontWeight: isSelected ? 700 : 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <span>{isSelected ? '✓' : '+'}</span>
                    <span>{tag.name}</span>
                    <span style={{ opacity: 0.6, fontSize: '9px', fontFamily: 'var(--font-mono)' }}>({tag.code})</span>
                  </button>
                );
              })}
            </div>

            {/* Custom Tag Input & Action Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
              <input
                type="text"
                placeholder="输入自定义标签代码并回车 (如 FACT_VOL_EXPANSION)..."
                value={customTagInput}
                onChange={(e) => setCustomTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addCustomTag();
                }}
                className="regime-search-input"
                style={{ flex: 1, height: '30px', fontSize: '11px' }}
              />
              <button
                onClick={addCustomTag}
                className="toggle-btn"
                style={{ height: '30px', padding: '0 12px', fontSize: '11px' }}
              >
                添加标签
              </button>
              <button
                onClick={runAttributionEvaluation}
                disabled={isEvaluating}
                id="btn-run-attribution-eval"
                style={{
                  height: '30px',
                  padding: '0 18px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: isEvaluating ? 'not-allowed' : 'pointer',
                  background: 'var(--btn-primary-bg)',
                  color: 'var(--btn-primary-text)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Play size={13} />
                <span>{isEvaluating ? '评估计算中...' : '开始因果沙箱推演'}</span>
              </button>
            </div>
          </div>

          {/* Evaluator Result Display */}
          {evaluatorState === 'loading' ? (
            <div className="regime-table-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              正在沙箱中执行瞬态因果标签遍历与 14 bps 交易摩擦扣减...
            </div>
          ) : evaluatorState === 'disconnected' ? (
            <div
              style={{
                padding: '16px',
                borderRadius: '8px',
                background: 'var(--alert-bearish-bg)',
                border: '1px solid var(--alert-bearish-border)',
                color: 'var(--color-rose)',
                fontSize: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}>
                <AlertTriangle size={14} />
                <span>时序沙箱服务未就绪或时序连接中断 (DISCONNECTED)</span>
              </div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                当前评估请求失败，因果指标保持 null 零伪造，未伪造任何模拟数据。
              </div>
            </div>
          ) : evaluatorReport ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Telemetry Status Banner */}
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  background: 'var(--badge-info-bg)',
                  border: '1px solid var(--badge-info-border)',
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Radio size={14} style={{ color: 'var(--accent-cyan)' }} />
                  <span style={{ fontWeight: 800, color: 'var(--accent-cyan)' }}>
                    沙箱推演状态:
                  </span>
                  <span
                    className={`badge ${
                      evaluatorReport.status === 'fresh'
                        ? 'badge-bullish'
                        : evaluatorReport.status === 'stale'
                        ? 'badge-warning'
                        : evaluatorReport.status === 'disconnected'
                        ? 'badge-bearish'
                        : 'badge-neutral'
                    }`}
                    style={{ fontSize: '11px', padding: '2px 8px' }}
                  >
                    ● {evaluatorReport.status.toUpperCase()}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    ({evaluatorReport.status_reason})
                  </span>
                </div>
                <div style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                  指纹: {evaluatorReport.request_fingerprint.slice(0, 16)}... · 水印: {evaluatorReport.source_watermark_utc || '—'}
                </div>
              </div>

              {/* Notice for NEUTRAL or non-fresh states (Strict Invariant Guard) */}
              {(evaluatorReport.status !== 'fresh' || direction === 'NEUTRAL') && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '6px',
                    background: 'var(--bg-canvas)',
                    border: '1px solid var(--border-subtle)',
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Info size={12} style={{ color: 'var(--accent-cyan)' }} />
                    <strong>因果零伪造守卫</strong>
                  </span>
                  ：当前状态为 {evaluatorReport.status.toUpperCase()} 且方向为 {direction}。依据 ASOP 金融工程不变式，所有方向性前向指标（胜率、获利因子、收益率）与基准净值序列严格置空（null / []），坚决杜绝在非就绪状态下构造虚假胜率。
                </div>
              )}

              {/* KPI Metric Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>匹配事件数 / 样本柱</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--accent-cyan)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {evaluatorReport.matched_event_count} <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>/ {evaluatorReport.sample_bars}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {combinationMode} 组合 · {holdingBars} 根 Bar
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>有利走势胜率</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: isFreshDirectional ? 'var(--color-bullish)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {isFreshDirectional && evaluatorReport.favorable_proportion ? formatDecimalPercent(evaluatorReport.favorable_proportion) : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    扣除滑点与费率后判定
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>获利因子 (Profit Factor)</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: isFreshDirectional ? 'var(--accent-cyan)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {isFreshDirectional ? (evaluatorReport.profit_factor ?? 'N/A (零亏损)') : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    毛利总计 / |毛损总计|
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>净收益率 (-14.0 bps 摩擦)</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: isFreshDirectional ? 'var(--text-primary)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {isFreshDirectional && evaluatorReport.net_return_pct ? `${evaluatorReport.net_return_pct}%` : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: isFreshDirectional ? 'var(--color-bullish)' : 'var(--text-muted)', marginTop: '2px' }}>
                    毛收益: {isFreshDirectional && evaluatorReport.gross_return_pct ? `${evaluatorReport.gross_return_pct}%` : '—'}
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>最大回撤 (MDD)</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: isFreshDirectional ? 'var(--color-rose)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {isFreshDirectional && evaluatorReport.max_drawdown_pct ? `${evaluatorReport.max_drawdown_pct}%` : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    事件区间峰值回撤
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>前向期望值 (Expectancy)</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: isFreshDirectional ? 'var(--text-primary)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    {isFreshDirectional && evaluatorReport.forward_expectancy_bps ? `${evaluatorReport.forward_expectancy_bps} bps` : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    单事件平均期望基点
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>收益离散度 (Mean ± Std)</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: isFreshDirectional ? 'var(--text-secondary)' : 'var(--text-muted)', marginTop: '4px', fontFamily: 'var(--font-mono)' }}>
                    {isFreshDirectional && evaluatorReport.dispersion_mean_bps ? `${evaluatorReport.dispersion_mean_bps} ± ${evaluatorReport.dispersion_std_bps} bp` : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    样本波动标度
                  </div>
                </div>

                <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>基准净值起点</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--accent-cyan)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                    1000.00 <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-muted)' }}>USDT</span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    固定名义基准（非账户余额）
                  </div>
                </div>
              </div>

              {/* Notional Reference Index Series Table & Progression */}
              {isFreshDirectional && evaluatorReport.notional_reference_index_series && evaluatorReport.notional_reference_index_series.length > 0 && (
                <div className="regime-table-card" style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <div className="bilingual-card-title">
                        <span className="bilingual-card-title-zh" style={{ fontWeight: 700 }}>
                          名义基准净值序列
                        </span>
                        <span className="bilingual-card-subtitle-en">NOTIONAL REFERENCE CAPITAL INDEX SERIES</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        初始名义基准 1000.00 · 每事件单利复合累加 · 扣除双边 14.0 bps 摩擦
                      </div>
                    </div>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: 'var(--bg-canvas)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--accent-cyan)',
                      }}
                    >
                      最新净值: {evaluatorReport.notional_reference_index_series[evaluatorReport.notional_reference_index_series.length - 1][1]}
                    </span>
                  </div>

                  {/* Series Items Ledger */}
                  <div
                    style={{
                      maxHeight: '220px',
                      overflowY: 'auto',
                      borderRadius: '6px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-canvas)',
                    }}
                  >
                    <table className="regime-decision-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '80px', textAlign: 'center' }}>事件序号</th>
                          <th style={{ minWidth: '180px' }}>事件开盘因果时间 (UTC)</th>
                          <th style={{ minWidth: '140px', textAlign: 'right' }}>名义净值 (起点 1000.00)</th>
                          <th style={{ minWidth: '120px', textAlign: 'right' }}>净值变动状态</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evaluatorReport.notional_reference_index_series.map((item, idx) => {
                          const [ts, val] = item;
                          const cmp = compareDecimalStrings(val, '1000.00');
                          const isGain = cmp > 0;
                          const isPar = cmp === 0;

                          return (
                            <tr key={ts}>
                              <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)' }}>
                                #{idx + 1}
                              </td>
                              <td style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                {ts}
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '12px', color: 'var(--accent-cyan)' }}>
                                {val}
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                                <span
                                  style={{
                                    color: isGain
                                      ? 'var(--color-bullish)'
                                      : isPar
                                      ? 'var(--text-muted)'
                                      : 'var(--color-bearish)',
                                    fontWeight: 700,
                                  }}
                                >
                                  {isGain ? '▲ 增益' : isPar ? '— 持平' : '▼ 回撤'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="regime-table-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Lightbulb size={14} style={{ color: 'var(--accent-cyan)' }} />
                <span>请在上方勾选事实标签，然后点击 <strong>开始因果沙箱推演</strong> 按钮发起瞬态回测。</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* 4. SUB-TAB 3: AI DATASET EXPORT HUB */}
      {activeSubTab === 'dataset' && (
        <div className="regime-table-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box size={20} style={{ color: 'var(--accent-cyan)' }} />
            <div>
              <div className="bilingual-card-title">
                <h3 className="bilingual-card-title-zh" style={{ margin: 0, fontSize: '14px', fontWeight: 800 }}>
                  流式 AI 数据集标准化导出管道
                </h3>
                <span className="bilingual-card-subtitle-en">STREAMING DATASET EXPORT PIPELINE</span>
              </div>
              <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
                严格遵循 Fixed Split Manifest (v1_standard_70_15_15) · 因果时延与成熟度截断 · NDJSON 确定性流式导出
              </p>
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              borderRadius: '6px',
              background: 'var(--bg-canvas)',
              border: '1px solid var(--border-subtle)',
              fontSize: '11px',
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
            }}
          >
            <div>• <strong>划分标准 (Fixed Split)</strong>: 训练集 70% · 验证集 15% · 测试集 15% 严格时序先后切分，拒绝随机乱序泄露。</div>
            <div>• <strong>成熟度守卫 (Maturity Guard)</strong>: 剔除最后 {holdingBars} 根未来收益尚未成熟的 Bar，避免末端标签未定。</div>
            <div>• <strong>因果隔离 (W ≥ 3.0s)</strong>: 严格按照烛线闭合时间戳排序，支持流式 NDJSON 下载，最大单次 10,000 条。</div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {(['TRAIN', 'VAL', 'TEST'] as const).map((split) => {
              const exportUrl = `/api/v1/lab/export-ai-dataset?symbol=${symbol}&timeframe=${timeframe}&direction=${direction}&split=${split}&holding_bars=${holdingBars}&lookback_days=${lookbackPreset}`;
              return (
                <div
                  key={split}
                  style={{
                    flex: '1 1 200px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg-surface)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: '13px', color: 'var(--accent-cyan)' }}>
                      {split} 集 ({split === 'TRAIN' ? '70%' : '15%'})
                    </span>
                    <span className="strip-pill-badge" style={{ fontSize: '9px' }}>
                      NDJSON
                    </span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                    标的: {symbol} · 周期: {timeframe} · 窗口: 近 {lookbackPreset} 天
                  </div>
                  <a
                    href={exportUrl}
                    target="_blank"
                    rel="noreferrer"
                    download={`${symbol}_${timeframe}_${split.toLowerCase()}.ndjson`}
                    className="toggle-btn"
                    style={{
                      textAlign: 'center',
                      textDecoration: 'none',
                      marginTop: '4px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: 'var(--bg-card)',
                      borderColor: 'var(--badge-info-border)',
                      color: 'var(--accent-cyan)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                    }}
                  >
                    <Download size={12} />
                    <span>下载 {split} 数据集</span>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
